import { Link } from "react-router-dom";
import { PawPrint } from "lucide-react";

const UPDATED = "February 25, 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink">{title}</h2>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="border-b border-border bg-white/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="privacy-home">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Shelter Focus</span>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-ink">Sign in</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16 space-y-10">
        <div>
          <p className="label-caps mb-2">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Last updated: {UPDATED}</p>
        </div>

        <Section title="Who we are">
          <p>
            Shelter Focus (&quot;we&quot;, &quot;us&quot;, &quot;the app&quot;) is a volunteer management platform built for
            animal shelters. Each shelter operates its own private instance of the app. This policy explains
            what data we collect, how we use it, and the choices you have.
          </p>
        </Section>

        <Section title="Information we collect">
          <p>When you use Shelter Focus, we collect:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><span className="text-ink font-medium">Account information</span> — name, email address, phone number (optional), password (stored as a bcrypt hash — never in plain text).</li>
            <li><span className="text-ink font-medium">Volunteer activity</span> — shifts you sign up for, check-in / check-out times, hours logged, dogs walked, cats handled, and completion notes.</li>
            <li><span className="text-ink font-medium">Issue reports</span> — description, severity, animal referenced, and any photo you attach.</li>
            <li><span className="text-ink font-medium">Communication</span> — messages posted to your shelter&apos;s bulletin board.</li>
            <li><span className="text-ink font-medium">Technical data</span> — login timestamps, IP address for failed-login lockout, and standard server logs.</li>
          </ul>
          <p>
            We do <span className="text-ink font-medium">not</span> collect location tracking, contact lists,
            device identifiers for advertising, or any biometric data.
          </p>
        </Section>

        <Section title="How we use your information">
          <ul className="list-disc list-inside space-y-1">
            <li>To operate the app — schedule shifts, count hours, deliver bulletins, route issue reports to shelter staff.</li>
            <li>To keep your account secure — password hashing, brute-force lockout, and session tokens stored in secure HttpOnly cookies.</li>
            <li>To send transactional email — welcome / launch emails, password reset links, and issue-report notifications to admins. Delivered via Resend.</li>
            <li>To sync with third-party tools your shelter has connected — Galaxy Digital (volunteer roster and lifetime hours) and ShelterLuv (currently-available animals). No data is sent to these services beyond what&apos;s needed to look up records.</li>
          </ul>
          <p>We never sell your data, never share it with advertisers, and never use it to train external AI models.</p>
        </Section>

        <Section title="Data sharing">
          <p>
            Your data is visible only inside your shelter&apos;s Shelter Focus instance. Admins at your shelter
            can see your name, email, phone (if provided), hours, shifts, and any issue reports you file.
            Other volunteers can see limited public info (name, hours, dogs walked) via the bulletin board and
            shelter roster views.
          </p>
          <p>
            We use these processors to deliver the service: Resend (transactional email), Emergent Object Storage
            (photo hosting for issue reports), and MongoDB Atlas (data storage). Each is bound by industry-standard
            data-processing agreements.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We keep your account and activity records as long as you have an active volunteer account with your
            shelter. If a shelter admin removes you from the roster, your signups and dog-walk records are
            deleted immediately. You may request full deletion of your account by emailing us or asking your
            shelter&apos;s admin to remove you.
          </p>
        </Section>

        <Section title="Your rights">
          <ul className="list-disc list-inside space-y-1">
            <li><span className="text-ink font-medium">Access</span> — view your data in the Profile tab of the app.</li>
            <li><span className="text-ink font-medium">Correction</span> — update your name, email, or phone through the app or by asking your admin.</li>
            <li><span className="text-ink font-medium">Deletion</span> — ask your admin, or email us directly, and we will remove your record and associated activity.</li>
            <li><span className="text-ink font-medium">Portability</span> — your shelter can export your volunteer hours as a Galaxy Digital-compatible CSV at any time.</li>
          </ul>
        </Section>

        <Section title="Children">
          <p>
            Shelter Focus is intended for volunteers age 13 and older. If your shelter allows younger volunteers,
            a parent or guardian must create and manage the account. We do not knowingly collect personal
            information from children under 13.
          </p>
        </Section>

        <Section title="Security">
          <p>
            Passwords are hashed with bcrypt. Session tokens are stored in HttpOnly, Secure, SameSite cookies —
            never in browser storage. All traffic runs over HTTPS. Photos uploaded to issue reports are stored
            in access-controlled object storage; only signed-in admins can view them.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we make material changes, we&apos;ll update this page and notify volunteers via bulletin or email.
            Continued use of the app after a change means you accept the updated policy.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions or requests? Email <a href="mailto:hello@shelterfocus.app" className="text-fern underline">hello@shelterfocus.app</a>.
          </p>
        </Section>

        <div className="border-t border-border pt-6 flex flex-wrap gap-4 text-sm">
          <Link to="/terms" className="text-fern hover:underline">Terms of Service</Link>
          <Link to="/" className="text-muted-foreground hover:text-ink">Back to home</Link>
        </div>
      </main>
    </div>
  );
}
